import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Sparkles, Radio } from "lucide-react";
import { API } from "../lib/api";

const SUGGESTIONS = [
  "What is this podcast about?",
  "How do I share my story?",
  "How can I support the show?",
  "Where can I watch episodes?",
];

const GREETING = {
  role: "assistant",
  content:
    "Hey, welcome to The Other Side of Blue. I can tell you about the show, help you share your story, subscribe, donate, or find episodes. What's on your mind?",
};

export default function AskAssistant() {
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);
  const didMount = useRef(false);

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    const end = endRef.current;
    const container = end?.parentElement;
    if (container) container.scrollTop = container.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setInput("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionId, history }),
      });
      if (!res.ok || !res.body) throw new Error("Bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data: /, "").trim();
          if (!line) continue;
          let obj;
          try { obj = JSON.parse(line); } catch { continue; }
          if (obj.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + obj.delta };
              return copy;
            });
          } else if (obj.session_id) {
            setSessionId(obj.session_id);
          } else if (obj.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: "Sorry, I hit a snag. Please try again in a moment." };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't connect. Please try again." };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="ask" className="relative py-24 bg-[#0C1424] grain overflow-hidden" data-testid="ask-section">
      <div className="absolute -top-20 right-1/4 h-72 w-72 rounded-full bg-[#0066FF]/10 blur-3xl" />
      <div className="relative max-w-4xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-10">
          <div className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#0066FF] mb-3 flex items-center justify-center gap-2">
            <Sparkles className="h-4 w-4" /> AI Assistant
          </div>
          <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none">
            Ask The Other Side of <span className="text-[#0066FF] blue-glow">Blue</span>
          </h2>
          <p className="mt-4 text-[#94A3B8] max-w-xl mx-auto">
            Have a question about the show, or want to share your story? Ask away.
          </p>
        </div>

        <div className="rounded-2xl border border-[#0066FF]/25 bg-[#050B14] overflow-hidden blue-box-glow">
          <div className="h-[420px] overflow-y-auto p-5 sm:p-6 space-y-4" data-testid="ask-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" && (
                  <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0066FF]/15 border border-[#0066FF]/40">
                    <Radio className="h-4 w-4 text-[#0066FF]" />
                  </span>
                )}
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#0066FF] text-white rounded-br-sm"
                      : "bg-[#121D33] text-[#E2E8F0] rounded-bl-sm"
                  }`}
                  data-testid={`ask-message-${m.role}`}
                >
                  {m.content || (loading && i === messages.length - 1 ? (
                    <span className="inline-flex gap-1">
                      <span className="h-2 w-2 rounded-full bg-[#0066FF] animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="h-2 w-2 rounded-full bg-[#0066FF] animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="h-2 w-2 rounded-full bg-[#0066FF] animate-bounce" style={{ animationDelay: "300ms" }} />
                    </span>
                  ) : "")}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          {messages.length <= 1 && (
            <div className="px-5 sm:px-6 pb-3 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  data-testid="ask-suggestion"
                  className="rounded-full border border-[#0066FF]/30 text-[#94A3B8] hover:text-white hover:border-[#0066FF] transition-colors px-3.5 py-1.5 text-xs font-cond uppercase tracking-wide"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-[#0066FF]/15 p-3 flex items-center gap-2"
            data-testid="ask-form"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question..."
              data-testid="ask-input"
              className="flex-1 rounded-full bg-[#0C1424] border border-[#0066FF]/25 px-4 py-3 text-sm outline-none focus:border-[#0066FF]"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              data-testid="ask-send-button"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#0066FF] hover:bg-[#2B82FF] disabled:opacity-50 transition-colors"
            >
              <Send className="h-5 w-5 text-white" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
